import { type GridColDef, GridActionsCellItem, type GridSortModel } from '@mui/x-data-grid-pro';
import { Typography, Box, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import delete_icon_red from '/icons/delete_icon_red.png';
import { createRenderColumnHeader } from '../components/common/GridHeaderUtils';
import { applyDashValueFormatter, formatMissingText } from './missingValue';

export type LabelTemplateGridRow = {
    id: string | number;
    template_name: string;
    type: string;
    size: string;
    orientation: string;
    site_code: string;
    crop_code: string;
    data_points: string;
    is_default: boolean;
};

const getCropNameByCode = (code: string, crops: any[] = []) => {
    const crop = crops.find((c: any) => c.crop_xrd_oid === code);
    return crop ? crop.name : formatMissingText(code);
};

export function getLabelTemplateCols(classes: any, sortModel: GridSortModel = [], crops: any[] = []): GridColDef[] {
    const renderHeader = createRenderColumnHeader(sortModel);

    const columns: GridColDef[] = [
        {
            field: 'template_name',
            headerName: 'Template name',
            minWidth: 220,
            flex: 1.5,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
            renderCell: (params: any) => (
                <Typography className={classes.labelNoto14Bold400} sx={{ fontSize: '14px', color: '#1F2937', fontWeight: 500 }}>
                    {formatMissingText(params?.row?.template_name)}
                </Typography>
            ),
        },
        {
            field: 'type',
            headerName: 'Type',
            minWidth: 100,
            flex: 0.6,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
        },
        {
            field: 'size',
            headerName: 'Size',
            minWidth: 140,
            flex: 0.8,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
        },
        {
            field: 'orientation',
            headerName: 'Orientation',
            minWidth: 140,
            flex: 0.9,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
        },
        {
            field: 'site_code',
            headerName: 'Site',
            minWidth: 110,
            flex: 0.65,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
        },
        {
            field: 'crop_code',
            headerName: 'Crop',
            minWidth: 110,
            flex: 0.65,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
            renderCell: (params: any) => (
                <Typography className={classes.labelNoto14Bold400} sx={{ fontSize: '14px', color: '#1F2937', fontWeight: 500 }}>
                    {formatMissingText(getCropNameByCode(params?.row?.crop_code, crops))}
                </Typography>
            ),
        },
        {
            field: 'data_points',
            headerName: 'Data points',
            minWidth: 300,
            flex: 1.8,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
        },
        {
            field: 'is_default',
            headerName: 'Default',
            minWidth: 100,
            flex: 0.6,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
            renderCell: (params: any) => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    {params?.row?.is_default
                        ? <CheckIcon sx={{ color: '#16A34A', fontSize: 20 }} />
                        : <CloseIcon sx={{ color: '#DC2626', fontSize: 20 }} />}
                </Box>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            type: 'actions',
            sortable:false,
            headerClassName: classes.labelNoto14Bold700,
            renderHeader,
            getActions: () => [
                <Tooltip key="edit" title="Action not ready. Under development." arrow placement="top">
                    <span>
                        <GridActionsCellItem
                            icon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                            label="Edit"
                            disabled
                        />
                    </span>
                </Tooltip>,
                <Tooltip key="copy" title="Action not ready. Under development." arrow placement="top">
                    <span>
                        <GridActionsCellItem
                            icon={<ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />}
                            label="Copy"
                            disabled
                        />
                    </span>
                </Tooltip>,
                <Tooltip key="delete" title="Action not ready. Under development." arrow placement="top">
                    <span>
                        <GridActionsCellItem
                            icon={<img src={delete_icon_red} alt="Delete" width={16} height={16} style={{ opacity: 0.4 }} />}
                            label="Delete"
                            disabled
                        />
                    </span>
                </Tooltip>,
            ]
        }
    ];

    return applyDashValueFormatter(columns);
}
